import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from '../lib/api';

interface MonthBreakdown {
  year: number;
  month: number;
  evolution_h: number;
  bug_h: number;
  question_h: number;
  total_h: number;
  jours: number;
}

interface ClientStatsRow {
  id_client: number;
  nom_client: string;
  nb_jour_mois: number;
  months: MonthBreakdown[];
}

const MOIS_FR = ['', 'JANVIER', 'FÉVRIER', 'MARS', 'AVRIL', 'MAI', 'JUIN', 'JUILLET', 'AOÛT', 'SEPTEMBRE', 'OCTOBRE', 'NOVEMBRE', 'DÉCEMBRE'];

function fmtH(h: number): string {
  const heures = Math.floor(h);
  const minutes = Math.round((h - heures) * 60);
  return `${heures} H ${minutes.toString().padStart(2, '0')}`;
}

export function StatsClientsPage() {
  const [items, setItems] = useState<ClientStatsRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet<{ items: ClientStatsRow[] }>('/api/stats/clients')
      .then((d) => setItems(d.items))
      .catch((e) => setErr(e instanceof Error ? e.message : 'Erreur'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ marginBottom: 12 }}>
        <Link to="/" style={{ fontSize: 'var(--fs-xs)' }}>
          ← retour
        </Link>
      </div>
      <h1 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, marginBottom: 16 }}>📈 Statistiques clients</h1>
      {loading && <div style={{ color: 'var(--text-mute)' }}>Chargement…</div>}
      {err && <div style={{ color: 'var(--danger)' }}>{err}</div>}

      {items.length === 0 && !loading && !err && (
        <div style={{ color: 'var(--text-mute)', fontSize: 'var(--fs-sm)' }}>Aucune donnée disponible.</div>
      )}

      {items.map((c) => (
        <section key={c.id_client} style={{ marginBottom: 24 }}>
          {/* Conteneur defilable : sans lui le tableau deborde de la page sur
              telephone (les trois autres pages de stats en avaient deja un). */}
          <div className="table-scroll">
          <table
            style={{
              width: '100%',
              minWidth: 480,
              borderCollapse: 'collapse',
              fontSize: 'var(--fs-sm)',
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <thead>
              <tr style={{ background: 'var(--bg-soft)', textAlign: 'left' }}>
                <th style={th()}>{c.nom_client}</th>
                <th style={th('right')}>Évolutions</th>
                <th style={th('right')}>Bugs</th>
                <th style={th('right')}>Questions</th>
                <th style={th('right')}>Total</th>
                <th style={th('right')}>Journées</th>
                <th style={th('right')}>TMA MAX</th>
              </tr>
            </thead>
            <tbody>
              {c.months.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 10, color: 'var(--text-mute)', textAlign: 'center' }}>
                    Aucune saisie de temps sur les 24 derniers mois.
                  </td>
                </tr>
              )}
              {c.months.map((m) => {
                const overTma = c.nb_jour_mois > 0 && m.jours >= c.nb_jour_mois;
                return (
                  <tr key={`${m.year}-${m.month}`} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={td()}>{MOIS_FR[m.month]} {m.year}</td>
                    <td style={td('right')}>{fmtH(m.evolution_h)}</td>
                    <td style={td('right')}>{fmtH(m.bug_h)}</td>
                    <td style={td('right')}>{fmtH(m.question_h)}</td>
                    <td style={td('right')}>{fmtH(m.total_h)}</td>
                    <td style={{ ...td('right'), fontWeight: 700, color: overTma ? '#dc2626' : '#059669' }}>
                      {m.jours.toFixed(1)}
                    </td>
                    <td style={td('right')}>{c.nb_jour_mois > 0 ? c.nb_jour_mois.toFixed(1) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </section>
      ))}
    </div>
  );
}

function th(align: 'left' | 'right' = 'left'): React.CSSProperties {
  return {
    padding: '8px 10px',
    fontSize: 'var(--fs-xs)',
    textTransform: 'uppercase',
    fontWeight: 700,
    textAlign: align,
    color: 'var(--text-mute)',
  };
}

function td(align: 'left' | 'right' = 'left'): React.CSSProperties {
  return { padding: '8px 10px', textAlign: align };
}
